import { useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AutoAspectImage } from "../ui/AutoAspectImage";
import { SmartImage } from "../ui/SmartImage";
import { GalleryLightbox } from "./GalleryLightbox";

export const Gallery = ({ gallery, productName }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const dragStart = useRef(null);
  const dragged = useRef(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  if (!gallery || gallery.length === 0) return null;

  const arrowClass =
    "absolute top-1/2 -translate-y-1/2 grid place-items-center h-8 w-8 rounded-full bg-ivory/80 border border-beige text-charcoal opacity-0 transition-opacity duration-300 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-charcoal z-10";

  const openLightbox = () => {
    setLightboxIndex(selectedIndex);
    setLightboxOpen(true);
  };

  return (
    <div data-testid="pdp-gallery">
      {/* Main swipeable carousel */}
      <div className="relative group overflow-hidden rounded-sm bg-stone spotlight">
        <div
          ref={emblaRef}
          className="overflow-hidden cursor-zoom-in"
          role="region"
          aria-roledescription="carousel"
          aria-label={`${productName} images`}
          onPointerDown={(e) => {
            dragged.current = false;
            dragStart.current = { x: e.clientX, y: e.clientY };
          }}
          onPointerMove={(e) => {
            if (dragStart.current && Math.hypot(e.clientX - dragStart.current.x, e.clientY - dragStart.current.y) > 6) {
              dragged.current = true;
            }
          }}
          onClick={() => {
            if (!dragged.current) openLightbox();
          }}
        >
          <div className="flex">
            {gallery.map((g, i) => (
              <div
                key={i}
                data-testid={`pdp-gallery-slide-${i}`}
                className="min-w-0 shrink-0 grow-0 basis-full"
                role="group"
                aria-roledescription="slide"
                aria-label={`${g.label}, ${i + 1} of ${gallery.length}`}
              >
                <AutoAspectImage src={g.src} alt={`${productName} — ${g.label}`} label={g.label} />
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => emblaApi?.scrollPrev()}
          data-testid="pdp-gallery-prev"
          aria-label="Previous image"
          className={`${arrowClass} left-3`}
        >
          <ChevronLeft size={18} strokeWidth={1.6} />
        </button>
        <button
          onClick={() => emblaApi?.scrollNext()}
          data-testid="pdp-gallery-next"
          aria-label="Next image"
          className={`${arrowClass} right-3`}
        >
          <ChevronRight size={18} strokeWidth={1.6} />
        </button>

        <span className="absolute bottom-4 left-4 bg-ivory/90 backdrop-blur-sm text-charcoal text-[0.68rem] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full">
          {gallery[selectedIndex]?.label ?? gallery[0]?.label}
        </span>
      </div>

      {/* Thumbnails */}
      <div className="mt-4 grid grid-cols-5 gap-3" data-testid="pdp-thumbnails">
        {gallery.map((g, i) => (
          <button
            key={i}
            onClick={() => emblaApi?.scrollTo(i)}
            data-testid={`pdp-thumb-${i}`}
            aria-label={g.label}
            aria-pressed={i === selectedIndex}
            className={`relative overflow-hidden rounded-sm aspect-square transition-[border-color,opacity] duration-300 border ${
              i === selectedIndex ? "border-charcoal opacity-100" : "border-transparent opacity-60 hover:opacity-100"
            }`}
          >
            <SmartImage src={g.src} alt={g.label} label={g.label} className="h-full w-full object-cover" />
          </button>
        ))}
      </div>

      <GalleryLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        gallery={gallery}
        startIndex={lightboxIndex}
        onIndexChange={(i) => {
          setSelectedIndex(i);
          emblaApi?.scrollTo(i);
        }}
      />
    </div>
  );
};